import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, QueryList, ViewChildren } from '@angular/core';
import { ChatService, ChatMessage, ChatOption } from '../../services/chat.service';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { Observable, timestamp } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DeviceService } from '../../services/device.service';
import { HttpService } from '../../services/https.service';
import { URLUtils } from '../../urlUtils';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { FeedbackService } from '../../services/feedback.service';
import { FeedbackConfig, FeedbackCategory, FeedbackQuestion, FeedbackAnswer } from '../../interfaces/feedback.model';

@Component({
  selector: 'app-chat',
  standalone: false,
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})

export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('messageContainer') private messageContainer!: ElementRef;
  @ViewChildren('messageItem') private messageItems!: QueryList<ElementRef>;

  messages: ChatMessage[] = [];
  selectedOptions: string[] = [];
  activeCategory: string | null = null;
  showDetailsInput = false;
  showPatientForm = false;
  additionalDetails = '';
  patientDetailsForm: FormGroup;
  isActiveFlow = false;
  isChatEnded = false;
  isCategorySelectionDisabled = false;
  /** id of the "Selected Category: ..." message that started the currently active department flow, so it (and everything after it) can be dropped if the user switches departments mid-flow. */
  private activeFlowStartMessageId: string | null = null;
  othersSpecifyText = '';
  showOthersTextarea = false;
  newMessagesAdded = false;
  lastMessageCount = 0;
  private scrollTimeout: any = null;
  /** Time shown on the feedback greeting. Follows the clock while the greeting is the only message, and stays put once the conversation starts. */
  greetingTime: Date = new Date();
  private greetingClockTimer: any = null;
  id: any;
  patientid: any;
  departments: any[] = [];
  patientOptions: any;
  selectedYesNo: string | null = null;
  isYesNoClicked = false;
  isLastYesNoClicked = false;
  departmentId: any;
  departmentName: any;
  uid: any;
  hospitalName: string | null = null;
  isValidUid = false;
  miscDetails = '';
  showMiscDetailsInput = false;
  selectedCategoryMessage: string = '';
  misId: any;
  activeYesNoButton: 'Yes' | 'No' | null = null;
  activeLastYesNoButton: 'Yes' | 'No' | null = null;

  // Communication preferences popup properties
  showWhatsAppPopup = false;
  tempFormData: any = null;
  communicationMethod: 'sms' | 'email' | 'none' = 'none';
  communicationName = '';
  communicationContact = '';
  showNameError = false;
  showContactError = false;
  contactErrorMessage = '';
  ticketId: any;

  // NPS Dialog
  showNpsDialog: boolean = false;
  showNpsConfirmDialog: boolean = false;
  npsScore: number = 0;
  npsScores: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  npsPatientName: string = '';
  npsContact: string = '';
  npsComments: string = '';
  npsCharCount: number = 0;
  npsWordCount: number = 0;
  npsNameError: boolean = false;
  npsContactError: boolean = false;
  npsContactErrorMsg: string = '';
  genUID: any;

  npsNameErrorMsg: string = '';
  npsMobileError: boolean = false;
  npsMobileErrorMsg: string = '';
  npsEmailError: boolean = false;
  npsEmailErrorMsg: string = '';

  // NPS dialog copy — kept as bound properties (not template literals) so they can be
  // swapped for API-provided wording later without another template change.
  // Defaults match the previous hardcoded hospital wording exactly.
  npsScaleLowLabel: string = 'Very Poor';
  npsScaleHighLabel: string = 'Excellent';

  // ===========================================================================
  // Dynamic (API-driven) feedback configuration
  // ===========================================================================
  feedbackConfig: FeedbackConfig | null = null;
  isDynamicMode = false;
  configLoading = true;
  configError = false;
  /** Bound to the header title — falls back to "Patient Support" until/unless config overrides it. */
  feedbackUiTitle: string = 'Patient Support';

  activeDynamicCategory: FeedbackCategory | null = null;
  currentDynamicQuestionIndex = -1;
  dynamicAnswers: FeedbackAnswer[] = [];

  // Per-question transient input state for the dynamic question renderer.
  dynamicSelectedOptions: string[] = [];
  dynamicShowOthersInput = false;
  dynamicOthersText = '';
  dynamicTextAnswer = '';
  dynamicRatingValue: number | null = null;
  dynamicSubmitting = false;

  /** Typed value per detail question, keyed by question id. */
  detailValues: { [questionId: string]: string } = {};
  /** Validation message per detail question, keyed by question id. */
  detailErrors: { [questionId: string]: string } = {};

  constructor(
    private chatService: ChatService,
    private httpService: HttpService,
    private feedbackService: FeedbackService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {
    this.patientDetailsForm = this.fb.group({
      wardNo: ['', Validators.required],
      patientName: [''],
      contactInfo: ['']
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.hospitalName = this.safeGetRouteParam(params, 'name');
    });

    this.chatService.getMessages().subscribe(messages => {
      this.newMessagesAdded = messages.length > this.lastMessageCount;
      this.lastMessageCount = messages.length;
      this.messages = messages;

      if (this.newMessagesAdded) {
        this.queueScrollToBottom();
      }
    });

    this.chatService.getActiveFlowStatus().subscribe(isActive => {
      this.isActiveFlow = isActive;
    });

    this.loadFeedbackConfig();
    this.generteUUID();

    // Keep the greeting's time current while the user is still on the home screen.
    this.greetingClockTimer = setInterval(() => {
      if (this.messages.length <= 1) {
        this.greetingTime = new Date();
      }
    }, 1000);

    this.messageItems?.changes.subscribe(() => {
      if (this.newMessagesAdded) {
        this.scrollToBottom();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.greetingClockTimer) {
      clearInterval(this.greetingClockTimer);
    }
  }

  ngAfterViewChecked(): void {
    if (this.newMessagesAdded) {
      this.scrollToBottom();
      this.newMessagesAdded = false;
    }
  }

  generteUUID() {
    const id = uuidv4();
    this.genUID = id;
    // console.log(id);
  }

  private queueScrollToBottom(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  /**
   * Reads a route param defensively. Angular's Router already decodes path
   * segments when building the ParamMap, but a malformed percent-encoding
   * sequence in the raw URL (e.g. a lone "%") can still throw a
   * "URI malformed" error inside that decode step. Never let that crash the app —
   * treat it the same as a missing/invalid firm name (routes to the not-found state).
   */
  private safeGetRouteParam(params: ParamMap, key: string): string | null {
    try {
      return params.get(key);
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[ChatComponent] Malformed route parameter, could not decode:', err);
      }
      return null;
    }
  }

  /**
   * Whether to show the NPS pre-complaint rating dialog. Driven off
   * `complaintType` (sourced from `settings.type` in the API response) —
   * Petcare is the one vertical today that skips it. If a future vertical
   * needs the same treatment, prefer adding a dedicated
   * `settings.features.show_nps` API flag over hardcoding another type
   * string here.
   */
  private shouldShowNpsDialog(): boolean {
    return this.feedbackConfig?.complaintType?.toLowerCase() !== 'petcare';
  }

  /**
   * Single configuration call: GET /questions/{firmName}. Replaces the old
   * getDepartment() -> GET /departments/{firmName} call. Everything downstream
   * (NPS submission, complaint submission, communication preferences) is
   * untouched and keeps using this.uid exactly as before.
   */
  loadFeedbackConfig(): void {
    this.configLoading = true;
    this.configError = false;

    if (!this.hospitalName) {
      this.configLoading = false;
      this.configError = true;
      this.isValidUid = false;
      return;
    }

    this.feedbackService.getFeedbackConfig(this.hospitalName).subscribe({
      next: (config) => {
        this.feedbackConfig = config;
        this.uid = config.uid;
        this.isValidUid = true;
        this.feedbackUiTitle = config.ui.title;
        this.configLoading = false;
        this.isDynamicMode = config.isDynamic;

        this.startFlowFromConfig();

        // The NPS pre-complaint rating dialog is a hospital-specific step.
        // Petcare has no NPS step at all — it should land straight on the
        // department-selection screen that startFlowFromConfig() just
        // rendered underneath this overlay.
        this.showNpsDialog = this.shouldShowNpsDialog();
      },
      error: (error) => {
        console.error('Error fetching feedback configuration:', error);
        this.configLoading = false;
        this.configError = true;
        this.isValidUid = false;
      }
    });
  }

  /**
   * Starts (or restarts, on "raise another issue") the chat from the already
   * loaded configuration, without re-hitting the network.
   */
  private startFlowFromConfig(): void {
    if (!this.feedbackConfig) {
      return;
    }

    this.activeDynamicCategory = null;
    this.currentDynamicQuestionIndex = -1;
    this.dynamicAnswers = [];

    if (this.isDynamicMode) {
      this.chatService.startDynamicChat(this.feedbackConfig.ui.message, this.feedbackConfig.categories);
    } else {
      // Legacy hospital path — identical to the previous getDepartment() success handler.
      this.departments = this.feedbackConfig.categories.map((c: FeedbackCategory) => ({ id: c.id, name: c.name }));
      this.chatService.startChat();
      this.applyDepartmentIdsToLegacyOptions();
    }
  }

  /** Exact same category-name -> department-id matching the old getDepartment() did inline. */
  private applyDepartmentIdsToLegacyOptions(): void {
    this.messages = this.messages.map(message => {
      if (message.options && message.options.length) {
        return {
          ...message,
          options: message.options.map(option => {
            const matchingDept = this.departments.find(dept => dept.name === option.text);
            if (matchingDept) {
              return {
                ...option,
                id: matchingDept.id
              };
            }
            return option;
          })
        };
      }
      return message;
    });
  }

  private scrollToBottom(): void {
    if (!this.messageContainer) return;

    requestAnimationFrame(() => {
      try {
        const element = this.messageContainer.nativeElement;

        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      } catch (err) {
        console.error('Error in scrollToBottom:', err);
      }
    });
  }

  addMessage(content: string, type: 'user' | 'bot'): void {
    this.messages.push({
      id: new Date().getTime().toString(),
      type: type,
      content: content,
      timestamp: new Date(),
    });

    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  selectOption(option: ChatOption, isMultiSelect: boolean): void {
    if (option.text === "Miscellaneous") {
      this.misId = option.id;
      console.log('misId', this.misId);
      this.showMiscDetailsInput = true;
    }
    this.departmentName = option.text;

    if (this.isChatEnded) {
      return;
    }

    // Editing already-confirmed issues stays locked; unrelated to switching departments below.
    if (isMultiSelect && this.isCategorySelectionDisabled) {
      return;
    }

    if (isMultiSelect) {
      const index = this.selectedOptions.indexOf(option.text);

      if (index > -1) {
        this.selectedOptions.splice(index, 1);
        if (option.text === 'Others (Specify)') {
          this.showOthersTextarea = false;
          this.othersSpecifyText = '';
        }
      } else {
        this.selectedOptions.push(option.text);
        if (option.text === 'Others (Specify)') {
          this.showOthersTextarea = true;
        }
      }
    } else {
      // Picking a (possibly different) department mid-flow: drop whatever
      // progress belonged to the previously selected department first.
      this.discardActiveFlow();

      this.activeCategory = option.text;
      this.isActiveFlow = true;
      this.activeFlowStartMessageId = this.chatService.handleUserSelection(option).id;
    }

    this.newMessagesAdded = true;
    this.queueScrollToBottom();

    if (option.id && option.nextMessageId) {
      this.departmentId = option.id;
      localStorage.setItem('departmentId', JSON.stringify(this.departmentId));
      localStorage.setItem('uid', JSON.stringify(this.uid));
    }
  }

  submitDetails(): void {
    if (this.additionalDetails.trim()) {
      const message = `Received message: ${this.additionalDetails}`;
      this.chatService.addUserMessage(message);
      this.showDetailsInput = false;
      this.showMiscDetailsInput = false;
      //this.additionalDetails = '';
      this.showPatientForm = true;
      this.chatService.addBotMessage({
        content: 'Please fill in the details below to help us address the issue effectively'
      });
      this.miscDetails = '';
      this.newMessagesAdded = true;
      this.queueScrollToBottom();
    }
  }

  /**
   * Clears whatever in-progress answers/UI state belong to the currently
   * active department, without touching chat history or ending the chat.
   * Used when the user switches departments mid-flow, so the newly picked
   * department starts clean instead of inheriting stale selections.
   */
  private discardActiveFlow(): void {
    // Drop the abandoned department's messages (its "Selected Category: ..."
    // line and everything after it) so switching doesn't leave a dead-end
    // question sitting in the transcript.
    if (this.activeFlowStartMessageId) {
      this.chatService.truncateMessagesFrom(this.activeFlowStartMessageId);
      this.activeFlowStartMessageId = null;
    }

    this.selectedOptions = [];
    this.showDetailsInput = false;
    this.showMiscDetailsInput = false;
    this.showPatientForm = false;
    this.additionalDetails = '';
    this.patientDetailsForm.reset();
    this.isCategorySelectionDisabled = false;
    this.showOthersTextarea = false;
    this.othersSpecifyText = '';
    // Dynamic flow state reset — no-ops for legacy hospitals.
    this.activeDynamicCategory = null;
    this.currentDynamicQuestionIndex = -1;
    this.dynamicAnswers = [];
    this.dynamicSelectedOptions = [];
    this.dynamicShowOthersInput = false;
    this.dynamicOthersText = '';
    this.dynamicTextAnswer = '';
    this.dynamicRatingValue = null;
    this.dynamicSubmitting = false;
    this.detailValues = {};
    this.detailErrors = {};
  }

  resetChat(): void {
    this.activeCategory = null;
    this.activeFlowStartMessageId = null;
    this.selectedOptions = [];
    this.showDetailsInput = false;
    this.showMiscDetailsInput = false;
    this.showPatientForm = false;
    this.additionalDetails = '';
    this.patientDetailsForm.reset();
    this.isActiveFlow = false;
    this.isChatEnded = false;
    this.isCategorySelectionDisabled = false;
    // Dynamic flow state reset — no-ops for legacy hospitals.
    this.activeDynamicCategory = null;
    this.currentDynamicQuestionIndex = -1;
    this.dynamicAnswers = [];
    this.dynamicSelectedOptions = [];
    this.dynamicShowOthersInput = false;
    this.dynamicOthersText = '';
    this.dynamicTextAnswer = '';
    this.dynamicRatingValue = null;
    this.dynamicSubmitting = false;
    this.detailValues = {};
    this.detailErrors = {};
    // Replays from the already-fetched configuration instead of re-calling the API.
    this.startFlowFromConfig();
  }

  isOptionActive(option: ChatOption, isMultiSelect: boolean): boolean {
    return isMultiSelect ?
      this.selectedOptions.indexOf(option.text) > -1 :
      this.activeCategory === option.text;
  }

  confirmIssues(): void {
    if (this.selectedOptions.length > 0) {
      let issuesList = [...this.selectedOptions];

      const othersIndex = issuesList.indexOf('Others (Specify)');
      if (othersIndex !== -1 && this.othersSpecifyText.trim()) {
        issuesList[othersIndex] = `Others - ${this.othersSpecifyText.trim()}`;
      }

      const message = `Selected issues: ${issuesList.join(', ')}`;
      this.chatService.addUserMessage(message);
      this.chatService.addBotMessage({
        content: 'Would you like to add any more details?',
        showYesNo: true,
        showLastYesNo: false
      });

      this.isCategorySelectionDisabled = true;

      this.showOthersTextarea = false;
      this.othersSpecifyText = '';

      this.patientOptions = [...issuesList];

      this.newMessagesAdded = true;
      this.queueScrollToBottom();
    }
  }

  // ===========================================================================
  // Dynamic (API-driven) question flow.
  // ===========================================================================

  selectDynamicCategory(category: FeedbackCategory): void {
    if (this.isChatEnded) {
      return;
    }

    // Picking a (possibly different) category mid-flow: drop whatever
    // progress belonged to the previously selected category first.
    this.discardActiveFlow();

    this.activeCategory = category.name;
    this.departmentName = category.name;
    this.departmentId = category.id;
    localStorage.setItem('departmentId', JSON.stringify(this.departmentId));
    localStorage.setItem('uid', JSON.stringify(this.uid));

    this.activeDynamicCategory = category;
    this.isActiveFlow = true;

    this.activeFlowStartMessageId = this.chatService.handleDynamicCategorySelection(category).id;

    if (!category.questions || category.questions.length === 0) {
      // No questions configured for this category — skip straight to the
      // existing "add more details" step rather than showing a dead end.
      this.finishDynamicQuestions();
      return;
    }

    this.advanceDynamicQuestion();
  }

  private advanceDynamicQuestion(): void {
    if (!this.activeDynamicCategory) {
      return;
    }

    this.currentDynamicQuestionIndex++;
    const questions = this.activeDynamicCategory.questions;

    if (this.currentDynamicQuestionIndex >= questions.length) {
      this.finishDynamicQuestions();
      return;
    }

    this.dynamicSelectedOptions = [];
    this.dynamicShowOthersInput = false;
    this.dynamicOthersText = '';
    this.dynamicTextAnswer = '';
    this.dynamicRatingValue = null;

    const question = questions[this.currentDynamicQuestionIndex];

    if (question.type === 'unsupported') {
      // Skip unrenderable questions rather than showing broken UI.
      this.advanceDynamicQuestion();
      return;
    }

    this.chatService.addDynamicQuestionMessage(question, this.currentDynamicQuestionIndex, questions.length);
    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  toggleDynamicMultiOption(optionText: string): void {
    const index = this.dynamicSelectedOptions.indexOf(optionText);
    if (index > -1) {
      this.dynamicSelectedOptions.splice(index, 1);
    } else {
      this.dynamicSelectedOptions.push(optionText);
    }

    const question = this.currentDynamicQuestion;
    if (question && question.allowOther && optionText === question.otherLabel) {
      this.dynamicShowOthersInput = this.dynamicSelectedOptions.indexOf(optionText) > -1;
      if (!this.dynamicShowOthersInput) {
        this.dynamicOthersText = '';
      }
    }
  }

  isDynamicMultiOptionActive(optionText: string): boolean {
    return this.dynamicSelectedOptions.indexOf(optionText) > -1;
  }

  confirmDynamicMultiAnswer(): void {
    const question = this.currentDynamicQuestion;
    if (!question) {
      return;
    }
    if (this.dynamicSelectedOptions.length === 0) {
      return;
    }
    if (this.dynamicShowOthersInput && !this.dynamicOthersText.trim()) {
      return;
    }

    let finalValues = [...this.dynamicSelectedOptions];
    if (question.allowOther) {
      const otherIndex = finalValues.indexOf(question.otherLabel);
      if (otherIndex !== -1 && this.dynamicOthersText.trim()) {
        finalValues[otherIndex] = `${question.otherLabel} - ${this.dynamicOthersText.trim()}`;
      }
    }

    this.recordDynamicAnswer(question, finalValues, this.dynamicShowOthersInput ? this.dynamicOthersText.trim() : undefined);
  }

  selectDynamicSingleOption(optionText: string): void {
    const question = this.currentDynamicQuestion;
    if (!question) {
      return;
    }
    this.recordDynamicAnswer(question, optionText);
  }

  selectDynamicRating(score: number): void {
    const question = this.currentDynamicQuestion;
    if (!question) {
      return;
    }
    this.dynamicRatingValue = score;
    this.recordDynamicAnswer(question, score);
  }

  submitDynamicText(): void {
    const question = this.currentDynamicQuestion;
    if (!question) {
      return;
    }
    const value = this.dynamicTextAnswer.trim();
    if (question.required && !value) {
      return;
    }
    this.recordDynamicAnswer(question, value);
  }

  /** The question currently awaiting an answer, or null if none is active. Used by the template. */
  get currentDynamicQuestion(): FeedbackQuestion | null {
    if (!this.activeDynamicCategory || this.currentDynamicQuestionIndex < 0) {
      return null;
    }
    return this.activeDynamicCategory.questions[this.currentDynamicQuestionIndex] || null;
  }

  private recordDynamicAnswer(question: FeedbackQuestion, value: string | string[] | number, otherText?: string): void {
    this.dynamicAnswers.push({
      questionId: question.id,
      question: question.question,
      type: question.type,
      value,
      otherText
    });

    const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
    this.chatService.addUserMessage(displayValue);

    this.dynamicSelectedOptions = [];
    this.dynamicShowOthersInput = false;
    this.dynamicOthersText = '';

    this.advanceDynamicQuestion();
  }

  private finishDynamicQuestions(): void {
    const issuesList = this.dynamicAnswers.map(answer => {
      const val = Array.isArray(answer.value) ? answer.value.join(', ') : String(answer.value);
      return `${answer.question}: ${val}`;
    });

    // Rejoin the existing flow exactly where confirmIssues() leaves off.
    this.patientOptions = issuesList;
    this.isCategorySelectionDisabled = true;

    this.chatService.addBotMessage({
      content: 'Would you like to add any more details?',
      showYesNo: true,
      showLastYesNo: false
    });

    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  isYesNoButtonActive(button: 'Yes' | 'No'): boolean {
    return this.activeYesNoButton === button;
  }

  isLastYesNoButtonActive(button: 'Yes' | 'No'): boolean {
    return this.activeLastYesNoButton === button;
  }

  handleYesNo(answer: 'Yes' | 'No', isEndFlow?: boolean): void {
    if (this.isYesNoClicked) return;
    this.isYesNoClicked = true;

    this.activeYesNoButton = answer;
    this.chatService.addUserMessage(answer);

    if (isEndFlow) {
      if (answer === 'Yes') {
        this.resetChat();
        this.resetYesNO();
      } else {
        this.isChatEnded = true;
        this.chatService.handleEndFlow(this.uiClosing);
      }
      return;
    }

    if (answer === 'Yes') {
      this.showDetailsInput = true;
      this.showPatientForm = false;
      this.chatService.addBotMessage({
        content: 'Please provide more details about your issue.'
      });
    } else {
      this.showDetailsInput = false;
      this.showPatientForm = true;
      this.chatService.addBotMessage({
        content: 'Please fill in the details below to help us address the issue effectively'
      });
    }

    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  handleLastYesNo(answer: 'Yes' | 'No', isEndFlow?: boolean): void {
    if (this.isLastYesNoClicked) return;
    this.isLastYesNoClicked = true;

    this.activeLastYesNoButton = answer;

    this.chatService.addUserMessage(answer);
    if (isEndFlow) {
      if (answer === 'Yes') {
        this.resetChat();
        this.resetYesNO();
      } else {
        this.isChatEnded = true;
        this.chatService.handleEndFlow(this.uiClosing);
      }
      return;
    }

    if (answer === 'Yes') {
      this.showDetailsInput = true;
      this.showPatientForm = false;
    } else {
      this.showDetailsInput = false;
      this.showPatientForm = true;
      this.chatService.addBotMessage({
        content: 'Please fill in the details below to help us address the issue effectively'
      });
    }

    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  /**
   * The firm's detail questions - name, ward, phone, email - asked at the end
   * of the complaint. Which of them exist is the firm's configuration, so a
   * vertical without a ward simply has no ward question.
   */
  /** NPS dialog copy, from the API with safe fallbacks. */
  get npsTagline(): string {
    return this.feedbackConfig?.ui?.npsTagline || 'Compassion・Care・Trust';
  }

  get npsQuestion(): string {
    return this.feedbackConfig?.ui?.npsQuestion
      || 'How would you rate your overall experience with our services?';
  }

  get npsThanks(): string {
    return this.feedbackConfig?.ui?.npsThanks
      || 'Thank you for your valuable feedback. Would you like to continue?';
  }

  /** Post-submit copy, from the API with a safe fallback. */
  get uiThanks(): string {
    return this.feedbackConfig?.ui?.thanks
      || 'Thank you for sharing the details. Our team will get back to you soon. Would you like to raise another issue?';
  }

  /** End-of-conversation copy, from the API with a safe fallback. */
  get uiClosing(): string {
    return this.feedbackConfig?.ui?.closing || 'Thank you for contacting us! Have a great day!';
  }

  get detailQuestions(): FeedbackQuestion[] {
    return this.feedbackConfig?.details ?? [];
  }

  /** A firm with no detail questions falls back to the legacy hospital form. */
  get useDynamicDetailsForm(): boolean {
    return this.detailQuestions.length > 0;
  }

  detailPlaceholder(question: FeedbackQuestion): string {
    return question.required ? `${question.question}*` : `${question.question} (Optional)`;
  }

  /** Firms that collect contact in their own step after the details form. */
  get useContactPreference(): boolean {
    return this.feedbackConfig?.contactPreference === true;
  }

  onDetailInput(question: FeedbackQuestion): void {
    delete this.detailErrors[question.id];
  }

  /**
   * Submit handler for the simplified, API-driven details form (Petcare).
   * Petcare's config sends `email` and `phone_number` as two independent,
   * separately labelled fields (and no "Communication Preferences" step at
   * all) — that doesn't fit the hospital single-contact-field model
   * (`communicationContact` / `communicationMethod` / the WhatsApp popup),
   * so this posts to /complaints directly via submitDynamicComplaint()
   * rather than forcing both values through that single-contact path.
   * `required` is intentionally ignored per spec — the only validation is
   * format (valid email / valid 10-digit phone), and only when something
   * was actually typed into that field.
   */
  submitDynamicDetailsForm(): void {
    this.detailErrors = {};

    for (const question of this.detailQuestions) {
      const value = (this.detailValues[question.id] || '').trim();

      if (question.required && !value) {
        this.detailErrors[question.id] = `${question.question} is required`;
        continue;
      }
      if (value && question.type === 'email' && !this.isValidEmail(value)) {
        this.detailErrors[question.id] = 'Please enter a valid email address';
        continue;
      }
      if (value && question.type === 'phone' && !this.isValidPhone(value)) {
        this.detailErrors[question.id] = 'Please enter a valid 10-digit phone number';
      }
    }

    if (Object.keys(this.detailErrors).length > 0) {
      return;
    }

    if (this.useContactPreference) {
      this.communicationName = this.detailValueFor('patient');
      this.communicationContact = '';
      this.communicationMethod = 'none';
      this.showWhatsAppPopup = true;
      return;
    }

    this.submitDynamicComplaint();
  }

  /** The typed answer to the detail question feeding a given column, if any. */
  private detailValueFor(column: string): string {
    const question = this.detailQuestions.find(q => q.mapsTo === column);
    return question ? (this.detailValues[question.id] || '').trim() : '';
  }

  /**
   * The free text typed in the "would you like to add any more details?" step,
   * as the API's comments list. Empty when nothing was typed - an empty string
   * stored there renders as a blank Additional Information on the staff side.
   */
  private get additionalComments(): string[] {
    const details = (this.additionalDetails || '').trim();
    return details ? [details] : [];
  }

  /**
   * Every answer the patient gave - the department's questions and the detail
   * questions - in the one array the API validates against.
   */
  private buildAnswersPayload(): any[] {
    const answers: any[] = this.dynamicAnswers.map(answer => {
      const payload: any = { question_id: answer.questionId, answer: answer.value };
      if (answer.otherText) {
        payload.other_text = answer.otherText;
      }
      return payload;
    });

    for (const question of this.detailQuestions) {
      const value = (this.detailValues[question.id] || '').trim();
      if (value) {
        answers.push({ question_id: question.id, answer: value });
      }
    }

    return answers;
  }

  /**
   * Posts the complaint for the config-driven (Petcare) details form.
   * Same endpoint/payload shape as the hospital path's POST /complaints/{uid}
   * call, but built directly from the three independent fields above instead
   * of the hospital's single derived contact/method — email and phone can
   * both be present (and both notified) at once, and there is no ward/bed
   * number for Petcare, so that line is left out of the payload and out of
   * the "Received details" summary entirely.
   */
  private submitDynamicComplaint(): void {
    const wardNo = this.detailValueFor('ward_no');
    let patientName = this.detailValueFor('patient');
    let email = this.detailValueFor('patient_email');
    let phone = this.detailValueFor('patient_phone');

    if (this.useContactPreference) {
      const contact = this.communicationContact.trim();
      patientName = this.communicationName.trim() || patientName;
      email = this.communicationMethod === 'email' ? contact : '';
      phone = this.communicationMethod === 'sms' ? this.formatPhone(contact) : '';
    }

    const complaints = {
      "uid": this.uid,
      "ward_no": wardNo,
      "patient": patientName,
      "phone_number": phone,
      "email": email,
      "communication_method": email && phone ? 'both' : (email ? 'email' : (phone ? 'sms' : 'none')),
      "session_id": this.genUID,
      "post_nps": this.npsScore,
      "contact_info": email || phone,
      // What the pet parent typed in the "any more details?" step. It is the
      // Additional Information the staff screens show, so it has to travel
      // with the feedback rather than being dropped here.
      "comments": this.additionalComments,
      "answers": this.buildAnswersPayload(),
      "option": this.patientOptions,
      "group_id": this.departmentId
    };

    this.httpService.sendPostNPSRequest(URLUtils.complaints(this.uid), complaints).subscribe(
      (resp: any) => {
        this.ticketId = resp.complaint_id;

        if (phone) {
          const smsPayload = { name: patientName, ticketId: this.ticketId, regarding: this.departmentName, to: phone, type: 'sms' };
          this.http.post('http://localhost:4200/api/notification/send-complaint-status', smsPayload).subscribe({
            next: (r: any) => console.log('SMS notification sent successfully:', r),
            error: (err) => console.error('Error sending SMS notification:', err)
          });
        }
        if (email) {
          const emailPayload = { name: patientName, ticketId: this.ticketId, regarding: this.departmentName, to: email, type: 'email' };
          this.http.post('http://localhost:4200/api/notification/send-complaint-status', emailPayload).subscribe({
            next: (r: any) => console.log('Email notification sent successfully:', r),
            error: (err) => console.error('Error sending Email notification:', err)
          });
        }
      },
      (error) => {
        console.error('Error getting complaint ID:', error);
      }
    );

    const filledDetails: string[] = this.detailQuestions
      .filter(question => (this.detailValues[question.id] || '').trim())
      .map(question => `${question.question}: ${(this.detailValues[question.id] || '').trim()}`);

    const message = filledDetails.length
      ? `Received details:\n ${filledDetails.join('\n')}`
      : 'Received details.';
    this.chatService.addUserMessage(message);

    this.showPatientForm = false;

    this.chatService.addBotMessage({
      content: this.uiThanks,
      showLastYesNo: true,
      isEndFlow: true
    });

    this.isChatEnded = true;
    this.newMessagesAdded = true;
    this.queueScrollToBottom();
  }

  submitPatientForm(): void {
    if (this.patientDetailsForm.valid) {
      const details = this.patientDetailsForm.value;

      this.tempFormData = details;

      this.communicationName = details.patientName || '';
      this.communicationContact = details.contactInfo || '';  

      // Auto-detect method from prefilled value
      if (this.communicationContact) {
        this.onContactInputChange();
      }

      this.showWhatsAppPopup = true;
    }
  }

  closeWhatsAppPopup(): void {
    this.showWhatsAppPopup = false;
    this.communicationMethod = 'none';
    this.communicationName = '';
    this.communicationContact = '';
    this.showNameError = false;
    this.showContactError = false;
    this.contactErrorMessage = '';
  }

  onContactInputChange(): void {
    // Reset error when input changes
    this.showContactError = false;
    this.contactErrorMessage = '';

    // Auto-detect communication method based on input
    if (this.communicationContact.trim()) {
      if (this.isValidEmail(this.communicationContact)) {
        this.communicationMethod = 'email';
      } else if (this.isValidPhone(this.communicationContact)) {
        this.communicationMethod = 'sms';
      } else {
        this.communicationMethod = 'none';
      }
    } else {
      this.communicationMethod = 'none';
    }
  }

  // Phone validation helper
  /** Local numbers go out E.164, the way the legacy path always sent them. */
  formatPhone(contact: string): string {
    const digits = (contact || '').replace(/[^\d]/g, '');
    if (!digits) {
      return '';
    }
    return digits.startsWith('91') ? `+${digits}` : `+91${digits}`;
  }

  isValidPhone(phone: string): boolean {
    const cleanPhone = phone.replace(/\s+/g, '').replace(/[^\d]/g, '');
    return cleanPhone.length === 10 && /^\d+$/.test(cleanPhone);
  }

  submitCommunicationPreference(): void {
    // Reset error messages
    this.showNameError = false;
    this.showContactError = false;
    this.contactErrorMessage = '';

    // Validate based on contact input
    if (this.communicationContact.trim()) {
      let hasError = false;

      if (!this.communicationName.trim()) {
        this.showNameError = true;
        hasError = true;
      }

      // Validate contact - must be either valid email or valid phone
      const isEmail = this.isValidEmail(this.communicationContact);
      const isPhone = this.isValidPhone(this.communicationContact);

      if (!isEmail && !isPhone) {
        this.showContactError = true;
        this.contactErrorMessage = 'Please enter a valid email address or 10-digit phone number';
        hasError = true;
      }

      if (hasError) {
        return;
      }

      // Set communication method based on validated input
      if (isEmail) {
        this.communicationMethod = 'email';
      } else if (isPhone) {
        this.communicationMethod = 'sms';
      }
    } else {
      // No contact provided - user chose not to receive updates
      this.communicationMethod = 'none';
    }

    if (this.useDynamicDetailsForm) {
      this.showWhatsAppPopup = false;
      this.submitDynamicComplaint();
      return;
    }

    const details = this.tempFormData;

    // Prepare contact information based on method
    let contactInfo = '';
    let phoneNumber = '';
    let emailAddress = '';

    if (this.communicationMethod === 'sms') {
      // Format phone number
      phoneNumber = this.formatPhone(this.communicationContact);
      contactInfo = phoneNumber;
    } else if (this.communicationMethod === 'email') {
      emailAddress = this.communicationContact.trim();
      contactInfo = emailAddress;
    }

    const patientName = this.communicationContact.trim()
      ? this.communicationName
      : (details?.patientName || '');

    let complaints = {
      "uid": this.uid,
      "ward_no": (details?.wardNo || '').trim(),
      "patient": patientName,
      "phone_number": phoneNumber,
      "email": emailAddress,
      "communication_method": this.communicationMethod,
      "session_id": this.genUID,
     "post_nps": this.npsScore,
      "contact_info": contactInfo,
      "comments": this.departmentName === 'Miscellaneous' ? [] : this.additionalComments,
      "answers": this.buildAnswersPayload(),
      "option": this.departmentName === 'Miscellaneous' ? [this.additionalDetails] : this.patientOptions,
      "group_id": this.departmentName === 'Miscellaneous' ? this.misId : this.departmentId
    }

    this.httpService.sendPostNPSRequest(URLUtils.complaints(this.uid), complaints).subscribe(
      (resp: any) => {
        this.ticketId = resp.complaint_id;

        // Send notification based on communication method
        // Wrapped in try-catch so that if the notification API doesn't exist / fails
        // to even trigger, it can never break the complaint submission flow.
        try {
          if (this.communicationMethod === 'sms' && phoneNumber) {
            let smsPayload = {
              "name": patientName,
              "ticketId": this.ticketId,
              "regarding": this.departmentName,
              "to": phoneNumber,
              "type": "sms"
            };

            this.http.post('http://localhost:3000/api/notification/send-complaint-status', smsPayload)
              .subscribe({
                next: (resp: any) => {
                  console.log("SMS notification sent successfully:", resp);
                },
                error: (err) => {
                  console.error("Error sending SMS notification:", err);
                }
              });
          } else if (this.communicationMethod === 'email' && emailAddress) {
            let emailPayload = {
              "name": patientName,
              "ticketId": this.ticketId,
              "regarding": this.departmentName,
              "to": emailAddress,
              "type": "email"
            };

            this.http.post('http://localhost:3000/api/notification/send-complaint-status', emailPayload)
              .subscribe({
                next: (resp: any) => {
                  console.log("Email notification sent successfully:", resp);
                },
                error: (err) => {
                  console.error("Error sending Email notification:", err);
                }
              });
          }
        } catch (err) {
          console.error("Error triggering complaint status notification:", err);
        }
      },
      (error) => {
        console.error("Error getting complaint ID:", error);
      }
    );

    // Create filled details array
    const filledDetails: string[] = [];
    if ((details?.wardNo || '').trim()) {
      filledDetails.push(`Ward No / Bed No: ${(details.wardNo as string).trim()}`);
    }
    if (patientName?.trim()) {
      filledDetails.push(`Patient Name: ${patientName}`);
    }
    if (this.communicationMethod === 'sms' && phoneNumber?.trim()) {
      filledDetails.push(`Phone Number: ${phoneNumber}`);
      filledDetails.push('Updates: SMS Enabled');
    } else if (this.communicationMethod === 'email' && emailAddress?.trim()) {
      filledDetails.push(`Email: ${emailAddress}`);
      filledDetails.push('Updates: Email Enabled');
    }

    const message = `Received details:\n ${filledDetails.join('\n')}`;
    this.chatService.addUserMessage(message);
    this.showPatientForm = false;
    this.patientDetailsForm.reset();

    this.chatService.addBotMessage({
      content: this.uiThanks,
      showLastYesNo: true,
      isEndFlow: true
    });

    this.isChatEnded = true;
    this.newMessagesAdded = true;
    this.queueScrollToBottom();

    this.closeWhatsAppPopup();
    this.tempFormData = null;
  }

  goBack(): void {
    // Reload the current page (same path/URL) instead of navigating browser
    // history, so the user lands back on a fresh department-selection screen
    // for this same firm — equivalent to clicking the browser's refresh button.
    window.location.reload();
  }

  onlyAllowNumbers(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  resetYesNO() {
    this.isYesNoClicked = false;
    this.isLastYesNoClicked = false;
  }
  selectNpsScore(score: number): void {
    this.npsScore = score;
  }

  onNpsCommentInput(): void {
   this.npsCharCount = this.npsComments.length;
  }

  submitNpsFeedback(): void {

    this.npsNameError = false;
    this.npsContactError = false;

    // NAME VALIDATION (optional field)
    if (
      this.npsPatientName.trim() &&
      !this.isValidName(this.npsPatientName)
    ) {
      this.npsNameError = true;
      this.npsNameErrorMsg = 'Name should contain only letters';
      return;
    }

    // CONTACT VALIDATION (optional field)
    const contact = this.npsContact.trim();

    if (contact) {

      const isEmail = this.isValidEmail(contact);
      const isMobile = this.isValidMobile(contact);

      if (!isEmail && !isMobile) {
        this.npsContactError = true;

        // number typed but not 10 digits
        if (/^\d+$/.test(contact)) {
          this.npsContactErrorMsg =
            'Mobile number must be 10 digits';
        } else {
          this.npsContactErrorMsg =
            'Please enter a valid email address';
        }

        return;
      }
    }

    const npsPayload = {
      session_id: this.genUID,
      score: this.npsScore,
      patient_name: this.npsPatientName,
      patient_phone: this.isValidMobile(contact) ? contact : '',
      patient_email: this.isValidEmail(contact) ? contact : '',
      comment: this.npsComments
    };

    this.httpService.sendPostNPSRequest(
      URLUtils.nps(this.uid),
      npsPayload
    ).subscribe(
      (resp: any) => { },
      (error) => {
        console.error(error);
      }
    );

    this.showNpsDialog = false;
    this.showNpsConfirmDialog = true;
  }

  npsContinueToComplaint(): void {
    this.showNpsConfirmDialog = false;
  }

  npsGoBack(): void {
    this.showNpsConfirmDialog = false;
    window.history.back();
  }

  isValidName(name: string): boolean {
    const regex = /^[A-Za-z ]+$/;
    return regex.test(name.trim());
  }
  isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }
  isValidMobile(mobile: string): boolean {
    const regex = /^[0-9]{10}$/;
    return regex.test(mobile);
  }

  onNpsContactInput(event: any): void {
    this.npsContactError = false;

    let value = event.target.value;

    // If only numbers typed
    if (/^\d+$/.test(value)) {
      value = value.replace(/\D/g, '');

      if (value.length > 10) {
        value = value.slice(0, 10);
      }

      this.npsContact = value;
    }
  }

}